/**
 * T8302 · Unified `QueryCache.subscribe` listener.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1773-1781 — "One subscription, three handlers, race closed."
 *
 * Two independent design notes (T503 for the reverse-index `'updated'` diff
 * and T505 for the `pendingListKeys` `'added'` insert) MUST share a SINGLE
 * `qc.getQueryCache().subscribe(...)` registration. Two parallel
 * subscriptions could interleave with the same QueryCache event and
 * reopen the in-flight invalidate race the design closes.
 *
 * Failure mode this prevents:
 *
 *   1. A list query is mid-fetch (added → updated {isFetching:true} → ...).
 *   2. A NATS realtime event for a new entity arrives DURING the fetch.
 *   3. The reverse-index can't find the list yet (it's pending).
 *   4. WITH one subscription · pendingListKeys.has(hash) → invalidate is
 *      routed; the fetch's final 'updated' eventually clears pending and
 *      the reverse-index takes over.
 *   5. WITHOUT one subscription · the two `subscribe` callbacks fire in
 *      undefined order. If the reverse-index handler runs first on the
 *      final 'updated' event, it diffs against an empty reverse index;
 *      if pendingListKeys runs first, it deletes the pending entry before
 *      the reverse-index has populated. Either way the NATS event lands
 *      in a 5-50 ms gap where neither layer covers the list.
 *
 * Mandatory dispatch order (spec L1779-1780):
 *
 *   'added'   · pendingListKeys.onAdded, then reverseIndex.onAdded
 *               (order is independent — both inserts; the test pins
 *               pending first so a synchronous NATS dispatch fired
 *               during 'added' propagation sees pending=true).
 *   'updated' · pendingListKeys.onUpdated BEFORE reverseIndex.onUpdated.
 *               The pending delete MUST fire first because the diff in
 *               `reverseIndex.onUpdated` reads pending state to decide
 *               whether to short-circuit. Reversing the order leaks the
 *               evicted ID into pendingListKeys (acceptance #6).
 *   'removed' · pendingListKeys.onRemoved, then reverseIndex.onRemoved.
 *               Pending is cleared first so a synchronous re-add (rare
 *               but possible during HMR / store reset) doesn't observe
 *               the stale entry.
 */

import type { QueryCacheNotifyEvent, QueryClient } from '@tanstack/react-query';

/**
 * Per-event handler trio. The reverse-index and pending-list-keys
 * modules each expose ONE handler per event type, and this listener
 * fans out under a single subscription.
 */
export interface QueryCacheEventHandlers {
  onAdded(event: QueryCacheNotifyEvent): void;
  onUpdated(event: QueryCacheNotifyEvent): void;
  onRemoved(event: QueryCacheNotifyEvent): void;
}

export interface UnifiedListenerArgs {
  readonly reverseIndex: QueryCacheEventHandlers;
  readonly pendingListKeys: QueryCacheEventHandlers;
  /**
   * Optional resource cleanup for a query leaving TanStack's cache. Runs
   * after the ordered pending/reverse-index removals, inside this listener's
   * single QueryCache subscription.
   */
  readonly onQueryRemoved?: (event: QueryCacheNotifyEvent) => void;
  /**
   * Optional cross-tab cache-sync emit. Orthogonal to the reverse-index
   * / pending-list-keys race: it only READS the event and enqueues a
   * coalesced broadcast — it mutates no shared structure. Fanned out
   * LAST (after both ordered handlers) so it can never perturb the
   * dispatch-order invariant above, and stays inside the SAME single
   * `subscribe` registration so the "exactly one subscribe" acceptance
   * test still holds. Absent on platforms without a BroadcastChannel.
   */
  readonly onProjectionEvent?: (event: QueryCacheNotifyEvent) => void;
  /** Optional read-only session-fetch tracking (`cache/persist/session-fetch`). */
  readonly onSessionFetchEvent?: (event: QueryCacheNotifyEvent) => void;
  readonly broadcastOut?: Partial<QueryCacheEventHandlers>;
}

export interface UnifiedListener {
  /** Attach the unified listener to `qc`. Returns a dispose function. */
  attach(qc: QueryClient): () => void;
}

export function createUnifiedQueryCacheListener(args: UnifiedListenerArgs): UnifiedListener {
  return {
    attach(qc: QueryClient): () => void {
      // EXACTLY ONE `subscribe` call per QueryClient. The acceptance test
      // spies on `QueryCache.subscribe` to assert the count is 1.
      const dispose = qc.getQueryCache().subscribe((event: QueryCacheNotifyEvent) => {
        switch (event.type) {
          case 'added':
            args.pendingListKeys.onAdded(event);
            args.reverseIndex.onAdded(event);
            args.broadcastOut?.onAdded?.(event);
            break;
          case 'updated':
            // CRITICAL: pendingListKeys BEFORE reverseIndex on 'updated'.
            // The reverse-index diff reads pending state to decide if a
            // newly-resolved list needs a full insert vs an incremental
            // diff. If reverseIndex runs first, it diffs against stale
            // pending and leaks the evicted ID (acceptance #6).
            args.pendingListKeys.onUpdated(event);
            args.reverseIndex.onUpdated(event);
            // Cross-tab emit fans out LAST — read-only, mutates nothing.
            args.broadcastOut?.onUpdated?.(event);
            break;
          case 'removed':
            args.pendingListKeys.onRemoved(event);
            args.reverseIndex.onRemoved(event);
            args.onQueryRemoved?.(event);
            break;
          // All other QueryCacheNotifyEvent types ('observerAdded' /
          // 'observerRemoved' / 'observerOptionsUpdated' / 'observerResultsUpdated')
          // are intentionally ignored — they don't affect either of the
          // two structures this listener owns. A switch-default would
          // emit a noisy telemetry event per observer churn.
        }
        args.onProjectionEvent?.(event);
        args.onSessionFetchEvent?.(event);
      });
      return dispose;
    },
  };
}
